import { useState, type CSSProperties, type ReactNode } from "react";

/**
 * Keyed enter-transition wrapper for the App shell's screen area: changing
 * `viewKey` re-mounts the container (a fresh DOM node), which replays the
 * `.view-enter` CSS animation (theme.css — a quick fade + 8px rise) on every
 * tab/view switch. Re-mounting also resets the scroll position, so each screen
 * opens at its top instead of inheriting the previous tab's offset.
 * `prefers-reduced-motion` disables the animation at the CSS layer.
 *
 * CRITICAL: the rise animates `transform`, and a transform-animated element is
 * a CONTAINING BLOCK for `position: fixed` descendants — with `fill-mode:
 * both` the finished animation keeps applying forever, so the Lobby drawer /
 * bottom sheets (fixed inset-0 overlays inside the screens) would stay trapped
 * inside this box (inset below the app-shell padding + cut above the bottom
 * nav → "vertically centred" drawer). MEASURED in headless Chromium against
 * the built CSS. So the class is DROPPED on `animationend` — the entrance
 * plays, then the wrapper returns to a plain, containing-block-free div.
 * (Under `prefers-reduced-motion` the animation is `none`, so no end event
 * fires and none is needed — an animation-free element never traps `fixed`.)
 */
export function ViewFade({
  viewKey,
  style,
  children,
}: {
  viewKey: string;
  style?: CSSProperties;
  children: ReactNode;
}): ReactNode {
  // Inner component keyed by view: remounting resets `entered`, so every view
  // switch replays the entrance; state (not classList surgery) so a later
  // React re-render can't resurrect the class.
  return (
    <FadeBox key={viewKey} style={style}>
      {children}
    </FadeBox>
  );
}

function FadeBox({ style, children }: { style?: CSSProperties; children: ReactNode }): ReactNode {
  const [entered, setEntered] = useState(false);
  return (
    <div
      /* The app's ONE scroll container. Tagged so a screen that has to restore a scroll position
         can find it without knowing the shell's structure — walking up looking for an element
         that happens to overflow does not work under happy-dom, which runs no layout at all. */
      data-scroll-root=""
      className={entered ? undefined : "view-enter"}
      style={style}
      onAnimationEnd={(e) => {
        // Only OUR entrance (descendants' animations bubble up here too).
        if (e.target === e.currentTarget && e.animationName === "view-enter") setEntered(true);
      }}
    >
      {children}
    </div>
  );
}
